import * as React from 'react';
import { useCallback, useEffect } from 'react';

import { useFloating, autoUpdate, size, Placement, offset, Strategy } from '@floating-ui/react-dom';
import { Portal } from 'react-portal';
import styled from 'styled-components';

import {
    MenuList,
    MenuListProps,
    InteractionModes,
    MenuTypes,
    Option,
    OptionGroup,
    Section,
} from '../menu-list/menu-list';

type triggerEl = React.ForwardRefExoticComponent<
    {
        onClick: React.MouseEventHandler;
        chosenItem: string;
        disabled?: boolean;
        isOpen?: boolean;
    } & React.RefAttributes<HTMLButtonElement>
>;

export enum DropdownMenuActionTypes {
    CLOSED_MENU = 'CLOSED_MENU',
    UPDATE_VALUE = 'UPDATE_VALUE',
}

export interface ClosedMenuAction {
    type: DropdownMenuActionTypes.CLOSED_MENU;
}
export interface UpdateValueAction {
    type: DropdownMenuActionTypes.UPDATE_VALUE;
    payload: { value: string };
}

export type DropMenuActions = ClosedMenuAction | UpdateValueAction;

export type AnyAction = { type: string; payload?: Record<string, any> };
export type ActionHandler<TAction extends AnyAction> = (action: TAction | AnyAction) => void;

export type DropdownMenuProps = Omit<
    MenuListProps,
    | 'menuType'
    | 'focussedInputValue'
    | 'activeItem'
    | 'setActiveItem'
    | 'setChosenItem'
    | 'interactionMode'
    | 'setInteractionMode'
> & {
    open?: boolean;
    selectedItem?: string;
    Trigger: triggerEl;
    actionHandler: ActionHandler<DropMenuActions>;
    /** where to place the menu relative to the trigger element */
    placement?: Placement;
    /** constrain the menu width to the trigger element width */
    matchTriggerWidth?: boolean;
    /** constrain the menu width to the available space */
    constrainWidth?: boolean;
    /** constrain the menu height to the available space */
    constrainHeight?: boolean;
    disabled?: boolean;
};

interface DropdownMenuList extends MenuListProps {
    position: Strategy;
    x: number;
    y: number;
    maxHeight: number | 'none';
}

const DropdownMenuList = styled(MenuList)<DropdownMenuList>`
    z-index: 1020;
    position: ${(props) => props.position};
    left: ${(props) => `${props.x}px`};
    top: ${(props) => `${props.y}px`};
    max-height: ${(props) => (props.maxHeight === 'none' ? 'none' : `${props.maxHeight}px`)};
`;

interface MenuProps extends DropdownMenuList {
    isOpen: boolean;
    portalKey: string;
    closeMenu: () => void;
}

const Menu = React.forwardRef<HTMLElement, MenuProps>(
    (
        {
            dataId,
            position,
            x,
            y,
            maxHeight,
            menuScrollY,
            isOpen,
            node,
            portalKey,
            options,
            closeMenu,
            interactionMode,
            setInteractionMode,
            focussedInputValue,
            activeItem,
            setActiveItem,
            setChosenItem,
            hasFilter,
            setFilterValue,
            filterValue,
            isFilterFocussed,
            setIsFilterFocussed,
            headerText,
            density,
            fontWeight,
        },
        ref,
    ) => {
        if (!isOpen) {
            return <></>;
        }

        return (
            <Portal node={node} key={portalKey}>
                <DropdownMenuList
                    dataId={dataId}
                    ref={ref}
                    node={node}
                    options={options}
                    onClose={closeMenu}
                    interactionMode={interactionMode}
                    setInteractionMode={setInteractionMode}
                    focussedInputValue={focussedInputValue}
                    activeItem={activeItem}
                    setActiveItem={setActiveItem}
                    menuType={MenuTypes.NavMenu}
                    setChosenItem={setChosenItem}
                    hasFilter={hasFilter}
                    setFilterValue={setFilterValue}
                    filterValue={filterValue}
                    isFilterFocussed={isFilterFocussed}
                    setIsFilterFocussed={setIsFilterFocussed}
                    position={position}
                    x={x}
                    y={y}
                    maxHeight={maxHeight}
                    menuScrollY={menuScrollY}
                    headerText={headerText}
                    density={density}
                    fontWeight={fontWeight}
                />
            </Portal>
        );
    },
);

Menu.displayName = 'Menu';

/**
 * A custom dropdown menu that behaves like a nav. Items must be selected by click or enter key.
 */
export const DropdownMenu: React.FC<React.PropsWithChildren<DropdownMenuProps>> = ({
    options,
    node = document.body,
    selectedItem = undefined,
    Trigger,
    actionHandler,
    open = false,
    hasFilter,
    placement = 'bottom-start',
    matchTriggerWidth = false,
    constrainWidth = true,
    constrainHeight = true,
    disabled = false,
    headerText,
    density,
    fontWeight,
}) => {
    const focussedInputValue = React.useRef<string | undefined>(selectedItem);
    const [isOpen, setIsOpen] = React.useState(open);
    const [chosenItem, setChosenItem] = React.useState<string | undefined>(selectedItem);
    const [interactionMode, setInteractionMode] = React.useState<InteractionModes>(InteractionModes.Mouse);
    const [activeItem, setActiveItem] = React.useState<string>();
    const menuScrollY = React.useRef(0);
    const menuMaxHeight = React.useRef<number | 'none'>('none');
    const [filterValue, setFilterValue] = React.useState('');
    const [isFilterFocussed, setIsFilterFocussed] = React.useState(true);
    const [filteredOptions, setFilteredOptions] = React.useState(options);

    /**
     * unique key for the portal
     */
    const portalKey = 'drop-menu-portal';

    const clickButton: React.MouseEventHandler = () => {
        if (isOpen) {
            // if it's already open, don't open again. This allows clicking the button to close it when it's open.
            setIsOpen(false);
            return;
        }
        // set the active item back to the chosen item before opening
        if (chosenItem) {
            setActiveItem(chosenItem);
            focussedInputValue.current = chosenItem;
        }
        setIsOpen(true);
    };

    const choseItemHandler = ({ value }: { value: string }) => {
        setChosenItem(value);
        // a dropdown menu 'selects' an item without necessarily first closing the menu
        actionHandler({ type: DropdownMenuActionTypes.UPDATE_VALUE, payload: { value } });
    };

    const closeMenu = useCallback(() => {
        if (isOpen) {
            setIsOpen(false);
            actionHandler({ type: DropdownMenuActionTypes.CLOSED_MENU });
        }
    }, [isOpen, setIsOpen, actionHandler]);

    useEffect(() => {
        window.removeEventListener('resize', closeMenu);
        window.addEventListener('resize', closeMenu);

        return () => {
            window.removeEventListener('resize', closeMenu);
        };
    }, [closeMenu]);

    // when disabled close the menu
    useEffect(() => {
        if (disabled) {
            closeMenu();
        }
    }, [disabled, closeMenu]);

    useEffect(() => {
        // when selectedItem prop changes update the active item
        focussedInputValue.current = selectedItem;
        setActiveItem(selectedItem);
        setChosenItem(selectedItem);
    }, [selectedItem]);

    const isSection = (arg: any): arg is Section => {
        return arg?.type === 'section';
    };

    useEffect(() => {
        // Filter the options when the filter or options change
        const transformedFilterValue = filterValue.toLowerCase().trim();
        const isFilterMatch = (value: string) => value.toLocaleLowerCase().includes(transformedFilterValue);

        const filterChildOptions = (group: OptionGroup) => {
            // If the group label matches the filter, include all child options
            if (isFilterMatch(group.label)) {
                return group;
            }

            // Else only include child options that match the filter
            const filteredChildOptions = group.options.filter((childOption) => isFilterMatch(childOption.text));

            return {
                label: group.label,
                options: filteredChildOptions,
            } as OptionGroup;
        };

        const filterSectionOptions = (section: Section) => {
            const filteredSections = section.options
                .map((option) => (isOptionGroup(option) ? filterChildOptions(option) : option))
                .filter((option) =>
                    isOptionGroup(option) ? option.options.length : isFilterMatch(option.text),
                ) as Array<Option | OptionGroup>;
            return { type: 'section', options: filteredSections } as Section;
        };

        const filteredOptions = options
            .map((option) =>
                isOptionGroup(option)
                    ? filterChildOptions(option)
                    : isSection(option)
                    ? filterSectionOptions(option)
                    : option,
            )
            .filter((option) =>
                isOptionGroup(option)
                    ? option.options.length
                    : isSection(option)
                    ? option.options.length
                    : isFilterMatch(option.text),
            );

        setFilteredOptions(filteredOptions);
    }, [filterValue, options]);

    const isOptionGroup = (item: any): item is OptionGroup => {
        return item?.label !== undefined;
    };

    const { x, y, reference, floating, strategy } = useFloating({
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(5),
            size({
                apply({ rects, availableWidth, availableHeight, elements }) {
                    if (matchTriggerWidth) {
                        // constrain width to the trigger button width
                        Object.assign(elements.floating.style, {
                            width: `${rects.reference.width}px`,
                        });
                    }
                    if (constrainWidth) {
                        // constrain to the available space
                        Object.assign(elements.floating.style, {
                            maxWidth: `${availableWidth}px`,
                        });
                    }
                    if (constrainHeight) {
                        menuMaxHeight.current = availableHeight;
                    } else {
                        menuMaxHeight.current = 'none';
                    }
                },
            }),
        ],
    });

    return (
        <>
            <Trigger
                ref={reference}
                isOpen={isOpen}
                onClick={clickButton}
                chosenItem={chosenItem || ''}
                disabled={disabled}
            />
            <Menu
                dataId={'dropdown-menu'}
                isOpen={isOpen}
                ref={floating}
                position={strategy}
                x={x ?? 0}
                y={y ?? 0}
                maxHeight={menuMaxHeight.current}
                menuScrollY={menuScrollY}
                node={node}
                options={filteredOptions}
                closeMenu={closeMenu}
                interactionMode={interactionMode}
                setInteractionMode={setInteractionMode}
                focussedInputValue={focussedInputValue}
                activeItem={activeItem}
                setActiveItem={setActiveItem}
                menuType={MenuTypes.NavMenu}
                setChosenItem={choseItemHandler}
                hasFilter={hasFilter}
                setFilterValue={setFilterValue}
                filterValue={filterValue}
                isFilterFocussed={isFilterFocussed}
                setIsFilterFocussed={setIsFilterFocussed}
                portalKey={portalKey}
                headerText={headerText}
                density={density}
                fontWeight={fontWeight}
            />
        </>
    );
};

DropdownMenu.displayName = 'DropdownMenu';
